"use client";

import { useState, useRef, useEffect, useCallback, type TouchEvent as ReactTouchEvent } from "react";
import BalanceScaleIcon from "./BalanceScaleIcon";

interface PromptComposerProps {
  onSend: (prompt: string) => void;
  onStop?: () => void;
  // After Stop saves a partial, the Send button briefly transforms into a
  // Discard button so the user can drop the just-saved record without
  // navigating to it. `showDiscardInsteadOfSend` controls the swap;
  // `onDiscard` is the click handler. Both must be set for the swap to fire.
  // The first character typed clears showDiscardInsteadOfSend (handled
  // upstream) and the button reverts to Send.
  onDiscard?: () => void;
  showDiscardInsteadOfSend?: boolean;
  isStreaming: boolean;
  disabled?: boolean;
  placeholder?: string;
  onFilesSelected?: (files: File[]) => void;
  pendingAttachments?: File[];
  onRemoveAttachment?: (index: number) => void;
  onNewChat?: () => void;
  suggestedPrompt?: string | null;
  onAcceptSuggestion?: () => void;
  // Auto-judge send — the DEFAULT submit action (v1.6 Batch 7: the plain
  // Send button was removed; the judge button moved down into its slot and
  // Enter fires it). Falls back to onSend when judging isn't possible
  // (judgeByDefault false — single-provider mode / <2 providers selected).
  // Mirrors iOS sendPromptAndAutoJudge.
  onSendAndJudge?: (prompt: string) => void;
  // False when judging is impossible (<2 providers selected) — the submit
  // button then renders as a plain send (arrow icon) and fires onSend.
  judgeByDefault?: boolean;
  // One-shot send to the favorite (single) provider only. The always-lit
  // "A" button fires this. It is NOT a mode toggle — tapping it just sends
  // THIS query to the fave provider; the next submit is multi-provider judge
  // again. (v1.6 Batch 7, revised 2026-07-03.)
  onSendFavorite?: (prompt: string) => void;
  singleProviderAbbrev?: string;
  singleProviderColor?: string;
  /** A one-shot external draft request. Omit it to retain normal local state. */
  prefillRequest?: { text: string; id: number } | null;
}

export default function PromptComposer({
  onSend,
  onStop,
  onDiscard,
  showDiscardInsteadOfSend,
  isStreaming,
  disabled,
  placeholder = "Ask anything...",
  onFilesSelected,
  pendingAttachments,
  onRemoveAttachment,
  onNewChat,
  suggestedPrompt,
  onAcceptSuggestion,
  onSendAndJudge,
  judgeByDefault = true,
  onSendFavorite,
  singleProviderAbbrev,
  singleProviderColor,
  prefillRequest,
}: PromptComposerProps) {
  const [text, setText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!prefillRequest) return;
    setText(prefillRequest.text);
    textareaRef.current?.focus();
  }, [prefillRequest]);

  // Auto-grow textarea without collapsing — avoids jitter from ResizeObserver
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const lineHeight = 21; // ~14px font * 1.5 line-height
    const maxHeight = lineHeight * 5 + 24; // 5 lines + padding
    // Temporarily set to 0 to get the true scrollHeight, but only read —
    // don't write "auto" which causes a visible layout shift.
    const prevHeight = textarea.style.height;
    textarea.style.height = "0px";
    const targetHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = prevHeight; // restore immediately
    const currentHeight = parseInt(prevHeight) || 0;
    if (targetHeight !== currentHeight) {
      textarea.style.height = `${targetHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [text, adjustHeight]);

  // Unified submit — auto-judge is the DEFAULT (v1.6 Batch 7). Plain send
  // only when judging isn't possible (judgeByDefault false) or no judge
  // handler was supplied. Enter and the submit button both route here.
  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    if (judgeByDefault && onSendAndJudge) {
      onSendAndJudge(trimmed);
    } else {
      onSend(trimmed);
    }
    setText("");
    // Reset height after clearing
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    });
  }, [text, disabled, judgeByDefault, onSendAndJudge, onSend]);

  // One-shot send to the favorite provider only (the "A" button). Not a mode —
  // fires this single query to the fave, then the textarea clears like any send.
  const handleSendFavorite = useCallback(() => {
    if (!onSendFavorite) return;
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSendFavorite(trimmed);
    setText("");
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    });
  }, [text, disabled, onSendFavorite]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
      // Tab accepts suggested prompt
      if (e.key === "Tab" && suggestedPrompt && !text) {
        e.preventDefault();
        setText(suggestedPrompt);
        onAcceptSuggestion?.();
      }
      // Shift+Enter: default behavior (newline) — no need to handle
    },
    [handleSubmit, suggestedPrompt, text, onAcceptSuggestion]
  );

  // Touch swipe-right to accept suggested prompt
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    if (suggestedPrompt && !text) {
      touchStartX.current = e.touches[0].clientX;
    }
  }, [suggestedPrompt, text]);

  const handleTouchEnd = useCallback((e: ReactTouchEvent) => {
    if (touchStartX.current !== null && suggestedPrompt && !text) {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (dx > 50) {
        setText(suggestedPrompt);
        onAcceptSuggestion?.();
      }
    }
    touchStartX.current = null;
  }, [suggestedPrompt, text, onAcceptSuggestion]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onFilesSelected) {
      onFilesSelected(files);
    }
  }, [onFilesSelected]);

  return (
    <div
      className="prompt-composer"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={dragOver ? { borderColor: "var(--accent)", borderStyle: "solid", borderWidth: "2px" } : undefined}
    >
      {pendingAttachments && pendingAttachments.length > 0 && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", padding: "4px 0" }}>
          {pendingAttachments.map((file, i) => (
            <AttachmentChip key={i} file={file} index={i} onRemove={onRemoveAttachment} />
          ))}
        </div>
      )}
      {/* Button column top-aligned to textarea top: the "A" toggle sits at
          the textarea's top edge, the judge submit tight 4px below it.
          Anything taller than the 72px column (e.g. textarea grows when
          user types) just pushes the textarea down — buttons stay anchored
          at the top so submit can't drift below the textarea bottom. */}
      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
        <div style={{ position: "relative", flex: 1 }}>
          <textarea
            ref={textareaRef}
            className="prompt-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            placeholder={suggestedPrompt && !text ? " " : placeholder}
            disabled={disabled || isStreaming}
            rows={2}
            style={{ resize: "none", background: "transparent", position: "relative", zIndex: 1 }}
          />
          {suggestedPrompt && !text && (
            <div
              style={{
                position: "absolute",
                top: "1px",
                left: "1px",
                right: "1px",
                bottom: "1px",
                padding: "12px",
                color: "var(--text-tertiary)",
                pointerEvents: "none",
                whiteSpace: "pre-wrap",
                overflow: "hidden",
                fontSize: "14px",
                lineHeight: 1.5,
                fontFamily: "inherit",
              }}
            >
              {suggestedPrompt}
              <span style={{ fontSize: "11px", opacity: 0.4, marginLeft: "8px" }}>Tab ↹</span>
            </div>
          )}
        </div>
        {/* Stacked button column: optional New Chat → (in the normal state)
            the always-lit "A" fave-send button → submit (judge by default);
            OR Stop (streaming) / Discard (post-stop). The "A" button is a
            one-shot send to the favorite provider, NOT a mode toggle.
            All circles 34×34 with 4pt spacing — iOS PromptComposer.swift
            renders the same column shape. */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "center", marginBottom: "0px" }}>
          {onNewChat && (
            <button
              onClick={onNewChat}
              style={{
                width: "34px", height: "34px", borderRadius: "50%",
                border: "none", background: "var(--border)",
                color: "var(--accent)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all var(--transition-fast)",
                touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
              }}
              title="New chat"
              aria-label="New chat"
            >
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="10.5" height="10.5" rx="2" />
                <path d="M16 2L8 10" />
              </svg>
            </button>
          )}
          {/* "A" fave-send button — only in the normal (non-streaming,
              non-discard) state, above submit. ALWAYS lit in the favorite
              provider's color (it is not a toggle); tapping sends this one
              query to the fave provider only. Dims + disables on empty text,
              same as submit. */}
          {onSendFavorite && !isStreaming && !(showDiscardInsteadOfSend && onDiscard && !text.trim()) && (
            <button
              onClick={handleSendFavorite}
              disabled={!text.trim() || disabled}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                background: singleProviderColor || "var(--accent)",
                color: "#ffffff",
                cursor: text.trim() && !disabled ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all var(--transition-fast)",
                opacity: text.trim() && !disabled ? 1 : 0.5,
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
              title={`Send to ${singleProviderAbbrev || "the favorite provider"} only`}
              aria-label="Send to favorite provider only"
            >
              <span style={{ fontSize: "13px", fontWeight: 700, lineHeight: 1 }}>
                {singleProviderAbbrev || "A"}
              </span>
            </button>
          )}
          {isStreaming ? (
            // Streaming: only Stop is shown. Discard is intentionally NOT
            // available here — accidental Discard during a long stream loses
            // partial work the user may have wanted to keep. Stop preserves
            // partial; Discard becomes available afterwards (in the Send slot)
            // for one interaction so the swap is deliberate.
            <button
              onClick={onStop}
              className="prompt-send-btn"
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                background: "var(--error)",
                color: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                touchAction: "manipulation",
              }}
              title="Stop"
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
                <rect x="2" y="2" width="10" height="10" rx="1" />
              </svg>
            </button>
          ) : showDiscardInsteadOfSend && onDiscard && !text.trim() ? (
            // Post-Stop & textarea empty: Discard replaces Send. The moment
            // the user types anything, this branch becomes false and Send
            // takes over (the recentlyStoppedRecord arm itself clears on
            // the next sendMessage). Tapping Discard deletes the just-saved
            // record; on success the upstream state clears and Send returns.
            <button
              onClick={onDiscard}
              className="prompt-send-btn"
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "1px solid var(--error)",
                background: "transparent",
                color: "var(--error)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
              title="Discard the partial answer that was just stopped"
              aria-label="Discard the partial answer just saved"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          ) : (
            // Submit — auto-judge by default (balance-scale icon, fires
            // onSendAndJudge). When judging isn't possible (single-provider
            // mode / <2 providers), it degrades to a plain send (arrow
            // icon, fires onSend). Enter triggers the same handleSubmit.
            <button
              onClick={handleSubmit}
              className="prompt-send-btn"
              disabled={!text.trim() || disabled}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                background: text.trim() && !disabled ? "var(--accent)" : "var(--border)",
                color: text.trim() && !disabled ? "#ffffff" : "var(--text-tertiary)",
                cursor: text.trim() && !disabled ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all var(--transition-fast)",
                opacity: text.trim() && !disabled ? 1 : 0.5,
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
              title={judgeByDefault && onSendAndJudge ? "Send & Judge (Enter)" : "Send (Enter)"}
              aria-label={judgeByDefault && onSendAndJudge ? "Send and judge responses" : "Send"}
            >
              {judgeByDefault && onSendAndJudge ? (
                <BalanceScaleIcon
                  size={18}
                  color="currentColor"
                  strokeMultiplier={1.6}
                />
              ) : (
                <svg width="19" height="19" viewBox="0 0 18 18" fill="none">
                  <path d="M9 14V4M5 8L9 4l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Attachment chip with image thumbnail support ---

function AttachmentChip({
  file,
  index,
  onRemove,
}: {
  file: File;
  index: number;
  onRemove?: (index: number) => void;
}) {
  const isImage = file.type.startsWith("image/");
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "2px 8px",
        background: "var(--surface)",
        borderRadius: "6px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {isImage && thumbUrl ? (
        <img
          src={thumbUrl}
          alt={file.name}
          style={{
            width: "24px",
            height: "24px",
            borderRadius: "4px",
            objectFit: "cover",
          }}
        />
      ) : (
        <span style={{ fontSize: "14px", lineHeight: 1 }}>&#128206;</span>
      )}
      <span>{file.name}</span>
      <button
        onClick={() => onRemove?.(index)}
        style={{
          background: "none",
          border: "none",
          color: "var(--text-tertiary)",
          cursor: "pointer",
          padding: "0 2px",
          fontSize: "14px",
        }}
      >
        &times;
      </button>
    </div>
  );
}
